import styled from "styled-components/macro";
import type { Process } from "types/types";
import { MACHINE_BREAKDOWN_METRIC_WIDTH } from "../machineBreakdownChartUtils";
import { useIsDarkMode } from "store/atoms";
import { darkModeTextColor } from "constants/colors";
import { useVisibleMetrics } from "components/Dashboard/GroupMetrics/useVisibleMetrics";
import { useSelector } from "store/useSelector";
import { Tooltip } from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";

/**
 * row of metrics which are also being displayed on the Timeline column
 * (modify on Settings page or completely hide with METRICS_TO_HIDE in customers.ts)
 */
export function MachineBreakdownRowMetrics({
  process,
  isShiftSelected,
  selectedShift,
  showHeaders = false,
}: {
  process: Process;
  isShiftSelected?: boolean;
  selectedShift?: string | null;
  showHeaders?: boolean;
}) {
  const visibleMetrics = useVisibleMetrics();
  const [isDarkMode] = useIsDarkMode();

  // Get standard data dumps for metrics
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );

  // Start with standard data dumps
  const originalDataDump = dataDumpsFullRangeByProcess[process.id];

  // Create a derived display object for metrics that can include shift-filtered values
  const metricsDisplay = { ...originalDataDump };
  const { translate } = useFlightTranslation();

  return (
    <MetricsStyles $isDarkMode={isDarkMode} $showHeaders={showHeaders}>
      {visibleMetrics.TimelineColumn.map(
        ({ dataDumpKey, displayName, units }) => {
          const value = dataDumpKey && metricsDisplay?.[dataDumpKey];
          return (
            <div className="metricColumn" key={dataDumpKey}>
              {showHeaders && (
                <div className="metricHeader">{displayName || dataDumpKey}</div>
              )}
              <Tooltip
                title={
                  selectedShift
                    ? translate(
                        "* Line metrics unavailable in this mode -- use Analytics page instead."
                      )
                    : ""
                }
                placement="bottom-end"
              >
                <div className="processMetric">
                  {value === undefined || selectedShift
                    ? "--"
                    : Number(value).toFixed(0) + units}
                </div>
              </Tooltip>
            </div>
          );
        }
      )}
    </MetricsStyles>
  );
}

const MetricsStyles = styled.div<{
  $isDarkMode: boolean;
  $showHeaders?: boolean;
}>`
  display: flex;
  justify-content: center;

  .metricColumn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
  }

  .metricHeader {
    font-size: 12px;
    color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : "#666")};
    width: ${MACHINE_BREAKDOWN_METRIC_WIDTH}px;
    text-align: center;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .processMetric {
    color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : "black")};
    width: ${MACHINE_BREAKDOWN_METRIC_WIDTH}px;
    text-align: center;
  }
`;
